import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { servicesService } from '../services/services.service';
import ServiceDetailsModal from '../components/ServiceDetailsModal';
import { formatDateTime } from '../utils/helpers';

const Services = () => {
    const { user } = useAuth();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    });
    const [filters, setFilters] = useState({
        status: '',
        search: ''
    });
    const [selectedService, setSelectedService] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchServices = useCallback(async () => {
        if (!user?.vendor_id) return;
        
        try {
            setLoading(true);
            setError(null);
            
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(filters.status && { status: filters.status }),
                ...(filters.search && { search: filters.search })
            };

            const response = await servicesService.getServices(user.vendor_id, params);
            
            // Handle the API response structure with 'items' array
            const servicesData = response.items || [];
            setServices(servicesData);
            setPagination(prev => ({
                ...prev,
                total: response.total || 0,
                totalPages: response.pages || 0
            }));
        } catch (err) {
            setError(err.message || 'Failed to fetch services');
            console.error('Error fetching services:', err);
        } finally {
            setLoading(false);
        }
    }, [user?.vendor_id, pagination.page, pagination.limit, filters]);

    useEffect(() => {
        fetchServices();
    }, [fetchServices]);

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when filtering
    };

    const handleViewService = (service) => {
        setSelectedService(service);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedService(null);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'pending': { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
            'assigned': { color: 'bg-blue-100 text-blue-800', label: 'Assigned' },
            'driver_started': { color: 'bg-indigo-100 text-indigo-800', label: 'Driver Started' },
            'vendor_reached_location': { color: 'bg-purple-100 text-purple-800', label: 'Reached Location' },
            'photos_submitted': { color: 'bg-orange-100 text-orange-800', label: 'Photos Submitted' },
            'service_resolved': { color: 'bg-teal-100 text-teal-800', label: 'Service Resolved' },
            'service_complete': { color: 'bg-green-100 text-green-800', label: 'Completed' },
            'otw_service_center': { color: 'bg-cyan-100 text-cyan-800', label: 'OTW Service Center' },
            'reached_service_center': { color: 'bg-pink-100 text-pink-800', label: 'Reached Service Center' },
            'svc_photos_submitted': { color: 'bg-amber-100 text-amber-800', label: 'Service Photos Submitted' },
            'service_cancelled': { color: 'bg-red-100 text-red-800', label: 'Cancelled' },
            'vendor_not_accepted': { color: 'bg-gray-100 text-gray-800', label: 'Not Accepted' }
        };

        const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
        
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                {config.label}
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return formatDateTime(dateString);
    };

    if (!user?.vendor_id) {
        return (
            <div className="page-container">
                <div className="content-container">
                    <div className="card">
                        <div className="text-center py-12">
                            <p className="text-gray-600">Please log in as a vendor to view services.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="content-container">
                {/* Header */}
                <div className="card mb-4 md:mb-6">
                    <div>
                        <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                            Services Management
                        </h1>
                        <p className="text-gray-600 mt-1 text-sm md:text-base">
                            Manage your service offerings and availability
                        </p>
                    </div>
                </div>

                {/* Filters */}
                <div className="card mb-4 md:mb-6">
                    <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 items-stretch sm:items-end">
                        <div className="flex-1 min-w-0 sm:min-w-48">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Search
                            </label>
                            <input
                                type="text"
                                placeholder="Search services..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div className="w-full sm:w-auto sm:min-w-32">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Status
                            </label>
                            <select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="assigned">Assigned</option>
                                <option value="driver_started">Driver Started</option>
                                <option value="vendor_reached_location">Reached Location</option>
                                <option value="photos_submitted">Photos Submitted</option>
                                <option value="service_resolved">Service Resolved</option>
                                <option value="service_complete">Completed</option>
                                <option value="otw_service_center">OTW Service Center</option>
                                <option value="reached_service_center">Reached Service Center</option>
                                <option value="svc_photos_submitted">Service Photos Submitted</option>
                                <option value="service_cancelled">Cancelled</option>
                                <option value="vendor_not_accepted">Not Accepted</option>
                            </select>
                        </div>
                        <div className="w-full sm:w-auto">
                            <button
                                onClick={fetchServices}
                                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>

                {/* Services List */}
                <div className="card">
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="inline-flex items-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Loading services...
                            </div>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <div className="text-red-600 mb-4">
                                <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <p className="text-gray-600">{error}</p>
                            <button
                                onClick={fetchServices}
                                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Try Again
                            </button>
                        </div>
                    ) : services.length === 0 ? (
                    <div className="text-center py-12">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 mb-4">
                                <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No services found</h3>
                            <p className="text-gray-600">No services match your current filters.</p>
                        </div>
                    ) : (
                        <>
                            {/* Mobile card list */}
                            <div className="md:hidden space-y-3">
                                {services.map((service) => (
                                    <div
                                        key={service.id}
                                        className="border border-gray-200 rounded-lg p-4 bg-white"
                                    >
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                            <button
                                                onClick={() => handleViewService(service)}
                                                className="text-blue-600 hover:text-blue-800 font-semibold text-sm"
                                            >
                                                #{service.service_number}
                                            </button>
                                            {getStatusBadge(service.status)}
                                        </div>
                                        <div className="space-y-1 text-sm text-gray-700">
                                            <p><span className="font-medium">Customer:</span> {service.customer || 'N/A'}</p>
                                            <p><span className="font-medium">Phone:</span> {service.phone || 'N/A'}</p>
                                            <p><span className="font-medium">Vehicle:</span> {service.vehicle || 'N/A'}</p>
                                            <p><span className="font-medium">Type:</span> {service.service_type || 'N/A'}</p>
                                            <p><span className="font-medium">Driver:</span> {service.driver?.name || 'Unassigned'}</p>
                                            <p className="text-gray-500 truncate"><span className="font-medium text-gray-700">Location:</span> {service.operating_location || 'N/A'}</p>
                                            <p className="text-gray-500"><span className="font-medium text-gray-700">Created:</span> {formatDate(service.created_at)}</p>
                                        </div>
                                        <div className="mt-3 flex gap-2">
                                            <button
                                                onClick={() => handleViewService(service)}
                                                className="flex-1 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md"
                                            >
                                                View
                                            </button>
                                            {service.status === 'pending' && (
                                                <button
                                                    onClick={() => {/* TODO: Implement assign driver */}}
                                                    className="flex-1 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-md"
                                                >
                                                    Assign
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Desktop table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Service Number
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Customer
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Vehicle
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Service Type
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Driver
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Location
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Created
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {services.map((service) => (
                                            <tr key={service.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    <button
                                                        onClick={() => handleViewService(service)}
                                                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors"
                                                    >
                                                        #{service.service_number}
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <div>
                                                        <div className="font-medium">{service.customer || 'N/A'}</div>
                                                        <div className="text-gray-500">{service.phone || 'N/A'}</div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {service.vehicle || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {service.service_type || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {getStatusBadge(service.status)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {service.driver?.name || 'Unassigned'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {service.operating_location || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatDate(service.created_at)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <button
                                                        onClick={() => handleViewService(service)}
                                                        className="text-blue-600 hover:text-blue-900 mr-3"
                                                    >
                                                        View
                                                    </button>
                                                    {service.status === 'pending' && (
                                                        <button
                                                            onClick={() => {/* TODO: Implement assign driver */}}
                                                            className="text-green-600 hover:text-green-900"
                                                        >
                                                            Assign
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {pagination.totalPages > 1 && (
                                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                                    <div className="flex-1 flex justify-between sm:hidden">
                                        <button
                                            onClick={() => handlePageChange(pagination.page - 1)}
                                            disabled={pagination.page === 1}
                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(pagination.page + 1)}
                                            disabled={pagination.page === pagination.totalPages}
                                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Next
                                        </button>
                                    </div>
                                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                        <div>
                                            <p className="text-sm text-gray-700">
                                                Showing{' '}
                                                <span className="font-medium">
                                                    {((pagination.page - 1) * pagination.limit) + 1}
                                                </span>
                                                {' '}to{' '}
                                                <span className="font-medium">
                                                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                                                </span>
                                                {' '}of{' '}
                                                <span className="font-medium">{pagination.total}</span>
                                                {' '}results
                        </p>
                    </div>
                                        <div>
                                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                                <button
                                                    onClick={() => handlePageChange(pagination.page - 1)}
                                                    disabled={pagination.page === 1}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <span className="sr-only">Previous</span>
                                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                                
                                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                                    const pageNum = i + 1;
                                                    return (
                                                        <button
                                                            key={pageNum}
                                                            onClick={() => handlePageChange(pageNum)}
                                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                                pageNum === pagination.page
                                                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                            }`}
                                                        >
                                                            {pageNum}
                                                        </button>
                                                    );
                                                })}
                                                
                                                <button
                                                    onClick={() => handlePageChange(pagination.page + 1)}
                                                    disabled={pagination.page === pagination.totalPages}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <span className="sr-only">Next</span>
                                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                            </nav>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Service Details Modal */}
            <ServiceDetailsModal
                service={selectedService}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />
        </div>
    );
};

export default Services;
